/*
 * REMIND CUSTOM: department-menu
 * Vanilla web component for the mobile drawer's department tabs (e.g. Women / Men).
 * - Tabs switch the visible category panel IN PLACE (no page reload).
 * - The active department is remembered in a cookie, so the menu stays consistent
 *   across pages without relying on per-collection metafields.
 * - Navigation to a department's main page happens via that menu's own "Shop all"
 *   link (a normal link inside the panel), which the theme prefetches on its own.
 * - No Section Rendering API / AJAX -> immune to the "null" submenu bug in ROAR 5.3.3.
 * - Implements the WAI-ARIA tabs pattern (roving tabindex, arrow/Home/End keys).
 * Isolated on purpose (own customElements.define) so ROAR theme updates never touch it.
 */
(function () {
  var COOKIE = 'remind_dept';
  var MAX_AGE = 60 * 60 * 24 * 30; // 30 days

  function getCookie(name) {
    var match = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return match ? decodeURIComponent(match[1]) : '';
  }

  function setCookie(name, value) {
    document.cookie =
      name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + MAX_AGE + '; SameSite=Lax';
  }

  if (customElements.get('remind-department-menu')) return;

  customElements.define(
    'remind-department-menu',
    class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-dept]'));
        this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-dept-panel]'));

        var fallback =
          this.dataset.default || (this.tabs[0] && this.tabs[0].dataset.dept) || '';
        // Restore the shopper's last choice, else the default department (Women).
        var active = getCookie(COOKIE) || fallback;

        this.select(active, false);

        this.tabs.forEach(
          function (tab) {
            tab.addEventListener(
              'click',
              function () {
                this.select(tab.dataset.dept, true);
              }.bind(this),
              false
            );
          }.bind(this)
        );

        this.addEventListener('keydown', this.onKeydown.bind(this), false);
      }

      // Resolve a department key to one that actually has a panel; a stale cookie
      // (menu since renamed/removed) falls back to the first panel so a tab is always
      // highlighted and the cookie gets corrected.
      resolve(dept) {
        var has = this.panels.some(function (p) {
          return p.dataset.deptPanel === dept;
        });
        if (has) return dept;
        return (this.panels[0] && this.panels[0].dataset.deptPanel) || dept;
      }

      select(dept, persist) {
        var resolved = this.resolve(dept);
        this.showPanel(resolved);
        this.highlight(resolved);
        if (persist) setCookie(COOKIE, resolved);
      }

      showPanel(dept) {
        var shown = false;
        this.panels.forEach(function (panel) {
          var match = panel.dataset.deptPanel === dept;
          panel.hidden = !match;
          if (match) shown = true;
        });
        if (!shown && this.panels[0]) this.panels[0].hidden = false; // safety net
      }

      highlight(dept) {
        this.tabs.forEach(function (tab) {
          var on = tab.dataset.dept === dept;
          tab.classList.toggle('xx-dept-tab--active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          // Roving tabindex: only the active tab is in the tab order.
          tab.setAttribute('tabindex', on ? '0' : '-1');
        });
      }

      onKeydown(event) {
        var keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (keys.indexOf(event.key) === -1) return;
        if (this.tabs.indexOf(event.target) === -1) return; // only when a tab is focused
        event.preventDefault();

        var rtl = getComputedStyle(this).direction === 'rtl';
        var last = this.tabs.length - 1;
        var idx = this.tabs.indexOf(event.target);
        var next = idx;

        if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = last;
        } else {
          var forward =
            event.key === 'ArrowDown' || event.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
          next = forward ? idx + 1 : idx - 1;
          if (next < 0) next = last;
          if (next > last) next = 0;
        }

        var tab = this.tabs[next];
        if (tab) {
          this.select(tab.dataset.dept, true);
          tab.focus();
        }
      }
    }
  );
})();
